/**
 * Markt-Aktivität: welche Filialen pushen wie regelmäßig, wer ist still?
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { ExportMenu } from '../components/ExportMenu.tsx';
import { TableSkeleton } from '../components/Skeleton.tsx';
import { fmtCurrency, fmtNumber, fmtPercent } from '../lib/format.ts';
import { Icon } from '../components/Icon.tsx';

function fmtDateGerman(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

function daysAgo(iso: string | null): number {
  if (!iso) return -1;
  const d = new Date(iso);
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

export function AktivitaetPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['aktivitaet'],
    queryFn: api.marktaktivitaet,
  });

  const stilleMaerkte = (data?.rows ?? []).filter((r) => daysAgo(r.letzter_tag) > 2);
  const aktiveMaerkte = (data?.rows ?? []).filter((r) => daysAgo(r.letzter_tag) <= 2);
  const avgAnteil = data && data.rows.length > 0
    ? data.rows.reduce((s, r) => s + r.anteil_tage_pct, 0) / data.rows.length
    : 0;

  return (
    <div className="space-y-4 lg:space-y-6 w-full">
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          Aktive Märkte
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Welche Filialen pushen regelmäßig, wer hat Lücken oder ist gerade still?
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="kpi-tile">
          <div className="kpi-label">Filialen gesamt</div>
          <div className="kpi-value">{data?.total_stores ?? 0}</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-label">Aktiv (≤ 2 Tage)</div>
          <div className="kpi-value kpi-delta-up">{aktiveMaerkte.length}</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-label">Still (&gt; 2 Tage)</div>
          <div
            className={`kpi-value ${
              stilleMaerkte.length > 0 ? 'kpi-delta-down' : ''
            }`}
          >
            {stilleMaerkte.length}
          </div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-label">Ø Datenabdeckung</div>
          <div className="kpi-value">{fmtPercent(avgAnteil / 100, 1)}</div>
          <div className="text-2xs text-slate-500 mt-1">
            der möglichen Tage gepushed
          </div>
        </div>
      </div>

      {/* Stille Märkte separat hervorheben */}
      {stilleMaerkte.length > 0 && (
        <div className="card border-accent-500/30">
          <div className="card-header">
            <div>
              <h3 className="card-title text-accent-600 flex items-center gap-2">
                <Icon name="trendingDown" size={14} />
                Filialen ohne aktuelle Daten
              </h3>
              <p className="card-subtitle">
                Letzter Push liegt mehr als 2 Tage zurück
              </p>
            </div>
          </div>
          <div className="card-body">
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Filial-Nr</th>
                    <th>Name</th>
                    <th>Ort</th>
                    <th className="num">Letzter Push</th>
                    <th className="num">Tage her</th>
                  </tr>
                </thead>
                <tbody>
                  {stilleMaerkte.map((r) => (
                    <tr key={r.store_id}>
                      <td className="font-mono text-2xs">{r.store_number}</td>
                      <td className="font-medium">{r.store_name}</td>
                      <td className="text-slate-500">{r.ort}</td>
                      <td className="num">{fmtDateGerman(r.letzter_tag)}</td>
                      <td className="num font-semibold kpi-delta-down">
                        {daysAgo(r.letzter_tag)} d
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Vollständige Tabelle */}
      <div className="card">
        <div className="card-header">
          <div>
            <h3 className="card-title">Alle Filialen — Aktivitätsübersicht</h3>
            <p className="card-subtitle">
              Sortiert nach Datenabdeckung
            </p>
          </div>
          {data && (
            <ExportMenu
              rows={data.rows as unknown as Record<string, unknown>[]}
              columns={[
                { header: 'Filial-Nr', key: 'store_number' },
                { header: 'Name', key: 'store_name', width: 40 },
                { header: 'Ort', key: 'ort' },
                { header: 'Tage mit Daten', key: 'tage_mit_daten', numeric: true },
                { header: 'Letzter Push', key: 'letzter_tag', format: (v) => fmtDateGerman(v as string | null) },
                {
                  header: 'Gesamtumsatz (€)',
                  key: 'gesamtumsatz',
                  numeric: true,
                  format: (v) => fmtCurrency(Number(v)),
                },
                { header: 'Bons', key: 'gesamtbons', numeric: true },
              ]}
              filename="Markt-Aktivitaet"
              title="Markt-Aktivität"
            />
          )}
        </div>
        <div className="card-body">
          {isLoading ? (
            <TableSkeleton rows={12} />
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Filiale</th>
                    <th>Ort</th>
                    <th className="num">Tage mit Daten</th>
                    <th className="num">Abdeckung</th>
                    <th className="num">Letzter Push</th>
                    <th className="num">Umsatz</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.rows.map((r) => {
                    const days = daysAgo(r.letzter_tag);
                    const ok = days <= 2;
                    return (
                      <tr key={r.store_id}>
                        <td className="font-mono text-2xs text-slate-400">
                          {r.store_number}
                        </td>
                        <td className="font-medium truncate max-w-xs">
                          {r.store_name}
                        </td>
                        <td className="text-slate-500">{r.ort}</td>
                        <td className="num">{fmtNumber(r.tage_mit_daten)}</td>
                        <td className="num">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                              <div
                                className={`h-full ${
                                  r.anteil_tage_pct >= 80
                                    ? 'bg-positive-500'
                                    : r.anteil_tage_pct >= 50
                                      ? 'bg-warning-500'
                                      : 'bg-accent-500'
                                }`}
                                style={{ width: `${Math.min(r.anteil_tage_pct, 100)}%` }}
                              />
                            </div>
                            <span className="font-semibold tabular-nums text-2xs w-12 text-right">
                              {fmtPercent(r.anteil_tage_pct / 100, 0)}
                            </span>
                          </div>
                        </td>
                        <td
                          className={`num ${
                            ok ? 'text-slate-500' : 'kpi-delta-down font-semibold'
                          }`}
                        >
                          {fmtDateGerman(r.letzter_tag)}
                          {!ok && (
                            <span className="text-2xs ml-1">({days}d)</span>
                          )}
                        </td>
                        <td className="num text-slate-500">
                          {fmtCurrency(r.gesamtumsatz)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
